import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { BaseFormControlledModal } from "../../../../../../common/components/base_form_controlled_modal";
import FormContainer from "../../../../../../common/components/form_container";
import DefaultFormInput from "../../../../../../common/components/default_form_input";
import { FormDatePickerInput } from "../../../../../../common/components/masked_inputs/react_forms/form_date_picker_input";
import { Dialog, DialogActions, DialogContent, DialogTitle, styled } from "@mui/material";
import { DefaultFormCheckBox } from "../../../../../../common/components/default_form_checkbox";
import { DropDownFuncionario } from "../../../../../../common/components/dropdown_funcionario";
import { QualidadeCronogramaSGQForm, QualidadeCronogramaSGQSchema } from "../../../schemas/CronogramaSGQ/qualidade_acoes_melhorias";
import { CronogramaSGQLista } from "../../../../domain/models/cronogramaSGQ_lista";
import { DefaultFormAutocomplete } from "../../../../../../common/components/default_form_autocomplete";
import { CentroCustoDropDown } from "../../../../../../common/domain/models/centro_custo_dropdown";
import { useCallback, useEffect } from "react";
import { FormMoneyMaskedInput } from "../../../../../../common/components/masked_inputs/react_forms/form_money_masked_input";
import { LogTipoTabela } from "../../../../../../common/utils/log_tabela_tipo";
import PrimaryButton from "../../../../../../common/components/primary_button";
import { isNotNullOrUndefined } from "../../../../../../common/utils/utils";

// CronogramaSGQModal 
 
type Props = {
  open: boolean;
  saving: boolean;
  loading: boolean;
  selectedCronogramaSGQ: CronogramaSGQLista | undefined, 
  centroCustoLista: CentroCustoDropDown[],
  onClose: () => void;
  onSubmit: (e: QualidadeCronogramaSGQForm) => void;
  onOpenChangeHistory: (id: number, tabelaTipo: LogTipoTabela) => void;
};

export const CronogramaSGQModal = ({
  open, saving,loading, selectedCronogramaSGQ,centroCustoLista,
  onClose, onSubmit, onOpenChangeHistory
}: Props) => {

  const hookForm = useForm<QualidadeCronogramaSGQForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: zodResolver(QualidadeCronogramaSGQSchema),
  });
  const isResponsavelManual = hookForm.watch("ResponsavelCadastrado");

  useEffect(() => {
    if(open){
      hookForm.reset();
      if(selectedCronogramaSGQ){
        hookForm.setValue("Atividade", selectedCronogramaSGQ.Atividade);
        if(selectedCronogramaSGQ.DataPrevista) hookForm.setValue("DataPrevista", new Date(selectedCronogramaSGQ.DataPrevista))
        if(selectedCronogramaSGQ.DataRealizada) hookForm.setValue("DataRealizada", new Date(selectedCronogramaSGQ.DataRealizada));
        if(selectedCronogramaSGQ.DataRealizadaReal) hookForm.setValue("DataRealizadaReal", new Date(selectedCronogramaSGQ.DataRealizadaReal));
        if(selectedCronogramaSGQ.DataPrevistaReal) hookForm.setValue("DataPrevistaReal", new Date(selectedCronogramaSGQ.DataPrevistaReal));
        if(selectedCronogramaSGQ.IdFuncionario) hookForm.setValue("IdFuncionario", selectedCronogramaSGQ.IdFuncionario);
        
        hookForm.setValue("IdCentro_custo", selectedCronogramaSGQ.IdCentroCusto);
        hookForm.setValue("DescResponsavel", selectedCronogramaSGQ.DescResponsavel);
        hookForm.setValue("Observacao", selectedCronogramaSGQ.Observacao);
        hookForm.setValue("Item", selectedCronogramaSGQ.Item);
        hookForm.setValue("ValorPrevisto", selectedCronogramaSGQ.ValorPrevisto ?? null);
        hookForm.setValue("ValorRealizado", selectedCronogramaSGQ.ValorRealizado ?? null);
        hookForm.setValue("ResponsavelCadastrado", String(selectedCronogramaSGQ.ResponsavelCadastrado).toLowerCase() === 'false' ? true : false);
      }
    }else{
       hookForm.reset({ResponsavelCadastrado: false});
    }
  },[hookForm, open, selectedCronogramaSGQ]);

  const { dirtyFields } = hookForm.formState;
  useEffect(() => {
    if (dirtyFields.ResponsavelCadastrado) {
      if (isResponsavelManual === true) {
        hookForm.setValue("IdFuncionario", null as any, { shouldValidate: false }); 
      } else if (isResponsavelManual === false) {
        hookForm.setValue("DescResponsavel", "", { shouldValidate: false });
      }
    }
  }, [isResponsavelManual, dirtyFields.ResponsavelCadastrado, hookForm]);

  const handleChangeHistory = useCallback(() => {
      if (selectedCronogramaSGQ) {
        onOpenChangeHistory(selectedCronogramaSGQ.IdCronogramaSGQ, LogTipoTabela.CRONOGRAMA_SGQ);
      }
    }, [selectedCronogramaSGQ, onOpenChangeHistory]);
  
    return (
    <Dialog
      open={open}
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        component: 'form',
        onSubmit: hookForm.handleSubmit(onSubmit)
      }}
      sx={{ "& .MuiDialog-paper": { maxWidth: "1200px", width: "100%" } }}
    >
      <DialogTitle>
        Cadastro de CronogramaSGQ
      </DialogTitle>

      <DialogContent>
        <FormContainer title={"Cadastrar/Editar CronogramaSGQ"} arrangement="fullRow">
          <div className="flex flex-col gap-y-4 w-full pt-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DefaultFormInput
                name='Item'
                label='Nº'
                readOnly={true}
                reactHookForm={hookForm}
              />
            </div>

            <div className="w-full">
              <DefaultFormInput
                name='Atividade'
                label='Atividade'
                required={true}
                rows={4}
                multiline={true}
                reactHookForm={hookForm}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
              
              <div className="flex flex-row gap-x-2 items-center">
                <div className="pt-1"> 
                  <DefaultFormCheckBox
                    name='ResponsavelCadastrado'
                    label='Preencher?' 
                    reactHookForm={hookForm}
                  />
                </div>
                <div className="flex-1">
                  {hookForm.watch("ResponsavelCadastrado") === true ? (
                    <DefaultFormInput
                      name='DescResponsavel'
                      label='Nome do Responsável'
                      reactHookForm={hookForm}
                      required={true}
                    />
                  ) : (
                    <DropDownFuncionario
                      name="IdFuncionario"
                      readOnly={saving}
                      reactHookForm={hookForm}
                      label="Responsável"
                      required={true}
                    />
                  )}
                </div>
              </div>

              <div className="w-full">
                <DefaultFormAutocomplete
                  name='IdCentro_custo'
                  label='Centro de Custo'
                  newButtonLabel='Novo'
                  getOptionLabel={(x) => x.title}
                  getOptionKey={(x) => x.id}
                  options={centroCustoLista}
                  reactHookForm={hookForm}
                  onNew={() => { }}
                />
              </div>

            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              <FormDatePickerInput
                name='DataPrevista'
                label='Data de Início Previsto'
                reactHookForm={hookForm}
              />
              <FormDatePickerInput
                name='DataRealizada'
                label='Data de Término Previsto' 
                required={true}
                reactHookForm={hookForm}
              />
              <FormDatePickerInput
                name='DataPrevistaReal'
                label='Data de Início Real'
                reactHookForm={hookForm}
              />
              <FormDatePickerInput
                name='DataRealizadaReal'
                label='Data de Término Real'
                reactHookForm={hookForm}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormMoneyMaskedInput
                name='ValorPrevisto'
                label='Valor Previsto (R$)'
                largeNumbers={true}
                reactHookForm={hookForm}
              />
              <FormMoneyMaskedInput
                name='ValorRealizado'
                label='Valor Realizado (R$)'
                largeNumbers={true}
                reactHookForm={hookForm}
              />
            </div>

            <div className="w-full">
              <DefaultFormInput
                name='Observacao'
                label='Observação'
                rows={4}
                multiline={true}
                reactHookForm={hookForm}
              />
            </div>

          </div>
        </FormContainer>
      </DialogContent>

      <DialogActions>
          <div className="w-full h-full items-center justify-between flex p-4 gap-4">
            <div>
              {
                isNotNullOrUndefined(selectedCronogramaSGQ) &&
                <PrimaryButton
                  buttonColor="yellow"
                  onClick={handleChangeHistory}>
                  <span className="text-xs">
                    Historico de alterações
                  </span>
                </PrimaryButton>
              }
            </div>

            <div className="flex flex-row gap-2">
              <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
                <h1
                  className="text-sm text-black dark:text-white"
                  onClick={onClose}>
                  Cancelar
                </h1>
              </div>

              <div className="w-[120px]">
                {saving ? (
                  <PrimaryButton
                    buttonColor="primary-green">
                    Salvando...
                  </PrimaryButton>
                ) : (
                  <PrimaryButton
                    type="submit"
                    buttonColor="primary-green">
                    Salvar
                  </PrimaryButton>
                )}
              </div>
            </div>
          </div>
      </DialogActions>
    </Dialog>
  );
}